import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { NavDropdown } from "react-bootstrap";
import { Link, useLocation } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUserPlus,
  faSignInAlt,
  faSignOutAlt,
  faChartLine,
  faListAlt,
  faCalendarDay,
  faFileAlt,
  faUsers,
  faUser,
  faCog,
  faDatabase,
  faChevronLeft,
  faChevronRight,
} from "@fortawesome/free-solid-svg-icons";
import { logoutUser } from "../services/index";

const SIDEBAR_STORAGE_KEY = "tradejet_sidebar_collapsed";

const NavigationBar = () => {
  const auth = useSelector((state) => state.auth);
  const dispatch = useDispatch();
  const location = useLocation();
  const isAdmin = auth.role?.toUpperCase() === "ADMIN";

  const [collapsed, setCollapsed] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(SIDEBAR_STORAGE_KEY) || "false");
    } catch {
      return false;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(SIDEBAR_STORAGE_KEY, JSON.stringify(collapsed));
    } catch (e) {
      // ignore
    }
  }, [collapsed]);

  const toggleSidebar = () => setCollapsed((c) => !c);

  const logout = () => {
    dispatch(logoutUser());
  };

  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + "/");

  const guestLinks = (
    <div className="app-sidebar__bottom">
      <Link to={"/trade_app/register"} className="app-sidebar__link">
        <FontAwesomeIcon icon={faUserPlus} className="app-sidebar__icon" />
        Register
      </Link>
      <Link to={"/trade_app/login"} className="app-sidebar__link">
        <FontAwesomeIcon icon={faSignInAlt} className="app-sidebar__icon" />
        Login
      </Link>
    </div>
  );

  const userLinks = (
    <>
      <nav className="app-sidebar__nav">
        <Link
          to={"/trade_app/dailyTrades"}
          className={`app-sidebar__link ${isActive("/trade_app/dailyTrades") ? "app-sidebar__link--active" : ""}`}
        >
          <FontAwesomeIcon icon={faCalendarDay} className="app-sidebar__icon" />
          <span className="app-sidebar__link-label">Daily Trades</span>
        </Link>

        {isAdmin && (
          <Link
            to={"/trade_app/report"}
            className={`app-sidebar__link ${isActive("/trade_app/report") ? "app-sidebar__link--active" : ""}`}
          >
            <FontAwesomeIcon icon={faFileAlt} className="app-sidebar__icon" />
            <span className="app-sidebar__link-label">Back Test Report</span>
          </Link>
        )}

        <Link
          to={"/trade_app/trades"}
          className={`app-sidebar__link ${isActive("/trade_app/trades") ? "app-sidebar__link--active" : ""}`}
        >
          <FontAwesomeIcon icon={faListAlt} className="app-sidebar__icon" />
          <span className="app-sidebar__link-label">Trade List</span>
        </Link>

        <Link
          to={"/trade_app/Chart"}
          className={`app-sidebar__link ${isActive("/trade_app/Chart") ? "app-sidebar__link--active" : ""}`}
        >
          <FontAwesomeIcon icon={faChartLine} className="app-sidebar__icon" />
          <span className="app-sidebar__link-label">Chart</span>
        </Link>

        <Link
          to={"/trade_app/mtmReport"}
          className={`app-sidebar__link ${isActive("/trade_app/mtmReport") ? "app-sidebar__link--active" : ""}`}
        >
          <FontAwesomeIcon icon={faFileAlt} className="app-sidebar__icon" />
          <span className="app-sidebar__link-label">MTM Report</span>
        </Link>

        {isAdmin && (
          <NavDropdown
            title={
              <span className="app-sidebar__link app-sidebar__dropdown-toggle">
                <FontAwesomeIcon icon={faDatabase} className="app-sidebar__icon" />
                <span className="app-sidebar__link-label">Data Tools</span>
              </span>
            }
            id="data-tools-dropdown"
            className="app-sidebar__dropdown"
            align="start"
          >
            <NavDropdown.Item as={Link} to="/trade_app/marketDataCompare">
              Market Data Compare
            </NavDropdown.Item>
            <NavDropdown.Item as={Link} to="/trade_app/indexAnalysisCompare">
              Index Analysis Compare
            </NavDropdown.Item>
            <NavDropdown.Item as={Link} to="/trade_app/exitAnalyzer">
              Exit Analyzer
            </NavDropdown.Item>
            <NavDropdown.Item as={Link} to="/trade_app/exitItemsBrowser">
              Exit Items Browser
            </NavDropdown.Item>
            <NavDropdown.Item as={Link} to="/trade_app/entryDataPointBrowser">
              Entry Data Point Browser
            </NavDropdown.Item>
            <NavDropdown.Item as={Link} to="/trade_app/exitSnapshot">
              Exit Snapshot View
            </NavDropdown.Item>
            <NavDropdown.Item as={Link} to="/trade_app/expiry/exitSnapshot">
              Expiry Exit Snapshot
            </NavDropdown.Item>
            <NavDropdown.Item as={Link} to="/trade_app/nonexpiry/exitSnapshot">
              Non-Expiry Exit Snapshot
            </NavDropdown.Item>
            <NavDropdown.Item as={Link} to="/trade_app/entryFirst45">
              First 45 Min Entry
            </NavDropdown.Item>
            <NavDropdown.Item as={Link} to="/trade_app/entryLastHour">
              Last Hour Entry
            </NavDropdown.Item>
          </NavDropdown>
        )}

        {isAdmin && (
          <Link
            to={"/trade_app/users"}
            className={`app-sidebar__link ${isActive("/trade_app/users") ? "app-sidebar__link--active" : ""}`}
          >
            <FontAwesomeIcon icon={faUsers} className="app-sidebar__icon" />
            <span className="app-sidebar__link-label">Users</span>
          </Link>
        )}
      </nav>

      <div className="app-sidebar__bottom">
        <NavDropdown
          title={
            <span className="app-sidebar__user-trigger">
              <span className="app-sidebar__user-avatar">
                <FontAwesomeIcon icon={faUser} />
              </span>
              <span className="app-sidebar__user-name">{auth.username || "User"}</span>
              {isAdmin && <span className="app-sidebar__user-badge">ADMIN</span>}
            </span>
          }
          id="user-dropdown"
          align="end"
          className="app-sidebar__user-dropdown"
        >
          <NavDropdown.Item disabled className="app-sidebar__user-email">
            {auth.email}
          </NavDropdown.Item>
          <NavDropdown.Divider />
          <NavDropdown.Item as={Link} to="/trade_app/home">
            <FontAwesomeIcon icon={faCog} className="me-2" />
            Settings
          </NavDropdown.Item>
          <NavDropdown.Item onClick={logout}>
            <FontAwesomeIcon icon={faSignOutAlt} className="me-2" />
            Logout
          </NavDropdown.Item>
        </NavDropdown>
      </div>
    </>
  );

  return (
    <aside className={`app-sidebar ${collapsed ? "app-sidebar--collapsed" : ""}`}>
      <div className="app-sidebar__brand-row">
        <Link to={auth.isLoggedIn ? "/trade_app/home" : "/trade_app/"} className="app-sidebar__brand">
          <span className="app-sidebar__brand-icon">📈</span>
          <span className="app-sidebar__brand-text">TradeJet</span>
          <span className="app-sidebar__brand-tag">TJ</span>
        </Link>
        <button
          type="button"
          className="app-sidebar__toggle"
          onClick={toggleSidebar}
          aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          <FontAwesomeIcon icon={collapsed ? faChevronRight : faChevronLeft} />
        </button>
      </div>
      {auth.isLoggedIn ? userLinks : guestLinks}
    </aside>
  );
};

export default NavigationBar;
